import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { 
  sendMessage,
  getOrCreateConversation,
  getConversationMessages,
  getUserConversations,
  getConversationWithUser,
  markConversationMessagesRead,
  markMessageNotificationsRead,
  subscribeToConversationMessages,
  subscribeToGeneralMessages,
  unsubscribeFromMessages, 
  detectOffPlatformPatterns 
} from '../../../services/messagingService';
import toast from 'react-hot-toast';

const MessagingPanel = ({ contracts, userId, userType, initialUserId }) => {
  const [messageMode, setMessageMode] = useState('general'); // 'general' or 'contract'
  const [selectedContractId, setSelectedContractId] = useState('');
  // UUID of the active conversation (for both modes)
  const [selectedConvId, setSelectedConvId] = useState(null);
  // The other user in the active contract conversation
  const [contractConvUserId, setContractConvUserId] = useState(null);
  const [selectedConversationUserId, setSelectedConversationUserId] = useState('');
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false); // for contract messages
  const [messagesLoading, setMessagesLoading] = useState(false); // for general messages
  const [showWarning, setShowWarning] = useState(false);
  const [detectedPatterns, setDetectedPatterns] = useState([]);
  // Track synthetic (pre-message) conversations injected from ?userId= param
  const [syntheticConvUserId, setSyntheticConvUserId] = useState(null);
  const messagesEndRef = useRef(null);
  const subscriptionRef = useRef(null);
  // Ref so real-time callbacks always read the latest state values
  const selectedConversationUserIdRef = useRef(selectedConversationUserId);
  const selectedConvIdRef = useRef(selectedConvId);
  const messagesRef = useRef(messages);

  // Keep refs in sync
  useEffect(() => {
    selectedConversationUserIdRef.current = selectedConversationUserId;
  }, [selectedConversationUserId]);

  useEffect(() => {
    selectedConvIdRef.current = selectedConvId;
  }, [selectedConvId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Load all conversations once; split into general/contract client-side
  useEffect(() => {
    if (!userId) return;

    if (syntheticConvUserId && messagesRef.current.length === 0) {
      removeSyntheticConversation(syntheticConvUserId);
    }

    loadConversations();
    setupGeneralSubscription();

    return () => {
      if (subscriptionRef?.current) {
        unsubscribeFromMessages(subscriptionRef?.current);
      }
    };
  }, [userId]);

  // Auto-select conversation when initialUserId is provided
  useEffect(() => {
    if (initialUserId) {
      setMessageMode('general');
      setSelectedConversationUserId(initialUserId);
    }
  }, [initialUserId]);

  // After conversations are loaded, auto-open the initialUserId conversation.
  // If a real conversation already exists, set selectedConvId so messages load.
  // If not, inject a synthetic entry so the user can start a new conversation.
  useEffect(() => {
    if (!initialUserId || !userId) return;
    const existing = conversations.find(c => c.other_user_id === initialUserId && !c.contract_id);
    if (existing) {
      // Real conversation found — select it directly
      setSelectedConvId(existing.conversation_id);
    } else if (!conversations.some(c => c.other_user_id === initialUserId)) {
      // No conversation at all yet — inject a synthetic placeholder
      injectSyntheticConversation(initialUserId);
    }
  }, [conversations, initialUserId]);

  // Cleanup synthetic conversation on unmount if no messages were sent
  useEffect(() => {
    return () => {
      // Cleanup happens via state; nothing persisted so no DB cleanup needed
    };
  }, []);

  // Load conversation messages when the active conversation changes
  useEffect(() => {
    if (selectedConvId) {
      loadConversationMessages();
    }
  }, [selectedConvId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const injectSyntheticConversation = async (otherUserId) => {
    try {
      // Fetch the user's name from user_profiles
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('full_name, email, avatar_url')
        .eq('id', otherUserId)
        .single();
      
      const syntheticConv = {
        other_user_id: otherUserId,
        other_user_name: profile?.full_name || profile?.email || 'Unknown User',
        other_user_email: profile?.email || '',
        other_user_avatar: profile?.avatar_url || null,
        last_message_text: '',
        last_message_at: null,
        is_unread: false,
        _synthetic: true,
      };

      setSyntheticConvUserId(otherUserId);
      setConversations(prev => {
        // Don't double-inject
        if (prev.some(c => c.other_user_id === otherUserId)) return prev;
        return [syntheticConv, ...prev];
      });
    } catch (err) {
      console.error('Failed to inject synthetic conversation:', err);
    }
  };

  const removeSyntheticConversation = (otherUserId) => {
    setSyntheticConvUserId(null);
    setConversations(prev => prev.filter(c => !(c.other_user_id === otherUserId && c._synthetic)));
  };

  // When the selected conversation changes away from a synthetic entry with no messages, remove it.
  // convId is the conversation UUID from the conversations list (null for synthetic entries).
  const handleSelectConversation = (convUserId, convId = null) => {
    if (
      syntheticConvUserId &&
      selectedConversationUserId === syntheticConvUserId &&
      convUserId !== syntheticConvUserId &&
      messagesRef.current.length === 0
    ) {
      removeSyntheticConversation(syntheticConvUserId);
    }
    setSelectedConversationUserId(convUserId);
    setSelectedConvId(convId);
  };

  // ── Conversation loading helpers ─────────────────────────────────────────
  const loadConversationsAsync = async () => {
    const result = await getUserConversations(userId);
    if (result?.success) setConversations(result?.conversations || []);
    return result;
  };

  const loadConversations = async () => {
    try {
      setLoading(true);
      await loadConversationsAsync();
    } catch (error) {
      console.error('Load conversations error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Contract conversation init ────────────────────────────────────────────
  // Called only when a contract has NO existing conversation yet.
  // Creates the conversation and selects it so the user can start messaging.
  const initContractConversation = async (contract) => {
    try {
      const otherUserId = userType === 'expert'
        ? contract?.client_id
        : contract?.expert?.user_id;
      if (!otherUserId) return;

      const convResult = await getOrCreateConversation(otherUserId, contract.id);
      if (!convResult?.success) throw new Error(convResult?.error);

      // Reload conversations so the new one appears in the list
      await loadConversationsAsync();

      setSelectedConvId(convResult.conversationId);
      setSelectedConversationUserId(otherUserId);
    } catch (error) {
      console.error('Init contract conversation error:', error);
      toast?.error('Could not open contract conversation');
    }
  };

  const loadConversationMessages = async () => {
    try {
      setMessagesLoading(true);
      // Resolve convId — may be null for brand-new synthetic conversations
      let convId = selectedConvId;
      if (!convId && selectedConversationUserId) {
        const res = await getOrCreateConversation(selectedConversationUserId);
        if (res?.success) {
          convId = res.conversationId;
          setSelectedConvId(convId);
        }
      }
      if (!convId) return;

      const result = await getConversationMessages(convId);
      if (result?.success) {
        setMessages(result?.messages);
        markConversationMessagesRead(convId, userId);
        if (selectedConversationUserId) {
          markMessageNotificationsRead(userId, selectedConversationUserId);
          setConversations(prev =>
            prev.map(c =>
              c.other_user_id === selectedConversationUserId ? { ...c, is_unread: false } : c
            )
          );
        }
      }
    } catch (error) {
      console.error('Load conversation messages error:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const setupGeneralSubscription = () => {
    if (subscriptionRef?.current) unsubscribeFromMessages(subscriptionRef?.current);

    subscriptionRef.current = subscribeToGeneralMessages(userId, (newMessage) => {
      const currentConvUserId = selectedConversationUserIdRef.current;

      // Update message thread if this message belongs to the open conversation
      if (
        currentConvUserId &&
        (newMessage.sender_id === currentConvUserId || newMessage.receiver_id === currentConvUserId)
      ) {
        setMessages((prev) => {
          if (prev.find((m) => m.id === newMessage.id)) return prev;
          return [...prev, newMessage];
        });

        if (newMessage.sender_id === currentConvUserId) {
          markConversationMessagesRead(selectedConvIdRef.current, userId);
          markMessageNotificationsRead(userId, currentConvUserId);
          setConversations(prev =>
            prev.map(c =>
              c.other_user_id === currentConvUserId ? { ...c, is_unread: false } : c
            )
          );
        }
      }

      // Optimistically surface the new message in the conversations list and re-sort
      // Use conversation_id from the message for a precise, unambiguous match
      if (newMessage.conversation_id) {
        setConversations(prev => {
          const updated = prev.map(c => {
            if (c.conversation_id !== newMessage.conversation_id) return c;
            return {
              ...c,
              _synthetic: false,
              last_message_text: newMessage.message_text,
              last_message_at: newMessage.created_at,
              is_unread: newMessage.sender_id !== userId
                ? (c.other_user_id !== currentConvUserId)
                : c.is_unread,
            };
          });
          return [...updated].sort((a, b) => {
            if (!a.last_message_at) return 1;
            if (!b.last_message_at) return -1;
            return new Date(b.last_message_at) - new Date(a.last_message_at);
          });
        });
      }
    });
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMessageChange = (e) => {
    const text = e?.target?.value;
    setMessageText(text);
    const detection = detectOffPlatformPatterns(text);
    if (detection?.flagged) {
      setShowWarning(true);
      setDetectedPatterns(detection?.patterns);
    } else {
      setShowWarning(false);
      setDetectedPatterns([]);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText?.trim()) return;
    if (showWarning) {
      toast?.error('Message blocked: remove contact information before sending.');
      return;
    }
    if (messageText.length > 5000) {
      toast?.error('Message is too long (maximum 5,000 characters).');
      return;
    }

    try {
      let receiverId;
      if (messageMode === 'contract') {
        if (!selectedConvId || !selectedConversationUserId) {
          toast?.error('Please select a contract conversation');
          return;
        }
        receiverId = selectedConversationUserId;
      } else {
        if (!selectedConversationUserId) {
          toast?.error('Please select a user to message');
          return;
        }
        receiverId = selectedConversationUserId;
      }

      const result = await sendMessage({
        conversationId: selectedConvId || undefined,
        receiverId,
        messageText
      });

      if (!result?.success) throw new Error(result?.error);

      const sentText = messageText;
      setMessageText('');
      setShowWarning(false);
      setDetectedPatterns([]);

      // Persist the conversation ID we just sent in — needed for synthetic conversations
      if (result?.conversationId && !selectedConvId) {
        setSelectedConvId(result.conversationId);
      }

      // Optimistically append message to thread
      if (result?.message) {
        setMessages((prev) => {
          if (prev.find((m) => m.id === result.message.id)) return prev;
          return [...prev, result.message];
        });
      }

      // Optimistically update conversations list and re-sort — no full reload
      const convIdToUpdate = result?.message?.conversation_id || selectedConvId;
      if (convIdToUpdate) {
        const now = result?.message?.created_at || new Date().toISOString();
        setConversations(prev => {
          const updated = prev.map(c =>
            c.conversation_id === convIdToUpdate
              ? { ...c, _synthetic: false, last_message_text: sentText, last_message_at: now }
              : c
          );
          // Promote synthetic flag off if it was synthetic
          if (syntheticConvUserId === receiverId) setSyntheticConvUserId(null);
          // Sort by newest first
          return [...updated].sort((a, b) => {
            if (!a.last_message_at) return 1;
            if (!b.last_message_at) return -1;
            return new Date(b.last_message_at) - new Date(a.last_message_at);
          });
        });
      }
    } catch (error) {
      console.error('Send message error:', error);
      toast?.error(error?.message || 'Failed to send message');
    }
  };

  const activeContracts = contracts?.filter((c) => c?.status !== 'cancelled');
  // Split the unified conversations list into general vs contract
  const generalConversations = conversations.filter(c => !c.contract_id);
  const contractConversations = conversations.filter(c => !!c.contract_id);
  // Contracts that don't yet have a conversation row
  const contractConvIds = new Set(contractConversations.map(c => c.contract_id));
  const contractsWithoutConv = activeContracts?.filter(c => !contractConvIds.has(c.id)) ?? [];

  return (
    <div className="space-y-4">
      {/* Mode Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setMessageMode('general')}
          className={`px-4 py-2 font-medium transition-colors ${
            messageMode === 'general'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center gap-2">
            <Icon name="MessageSquare" className="w-4 h-4" />
            <span>General Messages</span>
          </div>
        </button>
        <button
          onClick={() => setMessageMode('contract')}
          className={`px-4 py-2 font-medium transition-colors ${
            messageMode === 'contract'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center gap-2">
            <Icon name="FileText" className="w-4 h-4" />
            <span>Contract Messages</span>
          </div>
        </button>
      </div>

      {/* Contract Messages Mode */}
      {messageMode === 'contract' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[600px]">
          {/* Contract Conversations List */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="p-3 bg-muted border-b border-border">
              <h3 className="font-semibold text-foreground">Contracts</h3>
            </div>
            <div className="overflow-y-auto h-[calc(100%-52px)]">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Icon name="Loader" className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : contractConversations.length === 0 && contractsWithoutConv.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No contracts found
                </div>
              ) : (
                <>
                  {/* Existing contract conversations */}
                  {contractConversations.map((conv) => {
                    const contract = activeContracts?.find(c => c.id === conv.contract_id);
                    const isActive = selectedConvId === conv.conversation_id;
                    const lastAt = conv.last_message_at ? new Date(conv.last_message_at) : null;
                    const now = new Date();
                    const isToday = lastAt && lastAt.toDateString() === now.toDateString();
                    const timeLabel = lastAt
                      ? isToday
                        ? lastAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                        : lastAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : '';
                    return (
                      <button
                        key={conv.conversation_id}
                        onClick={() => handleSelectConversation(conv.other_user_id, conv.conversation_id)}
                        className={`w-full p-3 text-left hover:bg-muted/50 transition-colors border-b border-border ${
                          isActive ? 'bg-muted' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-medium text-foreground text-sm truncate flex-1 mr-2">
                            {contract?.title || conv.other_user_name || 'Contract'}
                          </span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">{timeLabel}</span>
                        </div>
                        {contract && (
                          <p className="text-xs text-muted-foreground mb-0.5">{contract.contract_number}</p>
                        )}
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-xs truncate flex-1 ${
                            conv.is_unread ? 'text-foreground font-medium' : 'text-muted-foreground'
                          }`}>
                            {conv.last_message_text || 'No messages yet'}
                          </p>
                          {conv.is_unread && (
                            <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {/* Contracts without a conversation yet */}
                  {contractsWithoutConv.map((contract) => (
                    <button
                      key={contract.id}
                      onClick={() => initContractConversation(contract)}
                      className="w-full p-3 text-left hover:bg-muted/50 transition-colors border-b border-border"
                    >
                      <p className="font-medium text-foreground text-sm truncate">
                        {contract?.title || 'Untitled Contract'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {contract?.contract_number}
                      </p>
                      <div className="flex items-center gap-1 mt-1">
                        <Icon name="User" className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <p className="text-xs text-muted-foreground truncate">
                          {userType === 'expert'
                            ? (contract?.client?.full_name || contract?.client?.email || 'Unknown Client')
                            : (contract?.expert?.full_name || 'Unknown Expert')}
                        </p>
                      </div>
                      <span className="inline-block text-xs mt-1 px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                        Start conversation
                      </span>
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Message Thread */}
          <div className="md:col-span-2">
            {selectedConvId && selectedConversationUserId ? (
              <MessageThreadUI
                messages={messages}
                loading={messagesLoading}
                userId={userId}
                messageText={messageText}
                showWarning={showWarning}
                detectedPatterns={detectedPatterns}
                messagesEndRef={messagesEndRef}
                handleMessageChange={handleMessageChange}
                handleSendMessage={handleSendMessage}
              />
            ) : (
              <div className="h-full border border-border rounded-lg flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <Icon name="FileText" className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Select a contract to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* General Messages Mode */}
      {messageMode === 'general' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[600px]">
          {/* Conversations List */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="p-3 bg-muted border-b border-border">
              <h3 className="font-semibold text-foreground">Conversations</h3>
            </div>
            <div className="overflow-y-auto h-[calc(100%-60px)]">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Icon name="Loader" className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No conversations yet
                </div>
              ) : (
                conversations.filter(c => !c.contract_id).map((conv) => {
                  const isActive = selectedConversationUserId === conv.other_user_id;
                  const lastAt = conv.last_message_at ? new Date(conv.last_message_at) : null;
                  const now = new Date();
                  const isToday = lastAt && lastAt.toDateString() === now.toDateString();
                  const timeLabel = lastAt
                    ? isToday
                      ? lastAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                      : lastAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : '';
                  return (
                    <button
                      key={conv.conversation_id || conv.other_user_id}
                      onClick={() => handleSelectConversation(conv.other_user_id, conv.conversation_id)}
                      className={`w-full p-3 text-left hover:bg-muted/50 transition-colors border-b border-border ${
                        isActive ? 'bg-muted' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-foreground text-sm truncate flex-1 mr-2">
                          {conv.other_user_name || conv.other_user_email || 'Unknown User'}
                        </span>
                        <span className="text-xs text-muted-foreground flex-shrink-0">{timeLabel}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-xs truncate flex-1 ${conv.is_unread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                          {conv.last_message_text || (conv._synthetic ? 'Start a conversation…' : 'No messages')}
                        </p>
                        {conv.is_unread && (
                          <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Message Thread */}
          <div className="md:col-span-2">
            {selectedConversationUserId ? (
              <MessageThreadUI
                messages={messages}
                loading={messagesLoading}
                userId={userId}
                messageText={messageText}
                showWarning={showWarning}
                detectedPatterns={detectedPatterns}
                messagesEndRef={messagesEndRef}
                handleMessageChange={handleMessageChange}
                handleSendMessage={handleSendMessage}
              />
            ) : (
              <div className="h-full border border-border rounded-lg flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <Icon name="MessageSquare" className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Select a conversation to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Reusable Message Thread UI Component
const MessageThreadUI = ({
  messages,
  loading,
  userId,
  messageText,
  showWarning,
  detectedPatterns,
  messagesEndRef,
  handleMessageChange,
  handleSendMessage
}) => (
  <div className="border border-border rounded-lg overflow-hidden h-[600px] flex flex-col">
    {/* Messages Area */}
    <div className="flex-1 overflow-y-auto p-4 bg-muted/20 space-y-3">
      {loading ? (
        <div className="flex items-center justify-center h-full">
          <Icon name="Loader" className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : messages?.length === 0 ? (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <p>No messages yet. Start the conversation!</p>
        </div>
      ) : (
        messages?.map((message) => {
          const isSender = message?.sender_id === userId;
          return (
            <div
              key={message?.id}
              className={`flex ${isSender ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[70%] rounded-lg p-3 ${
                  isSender
                    ? 'bg-primary text-white'
                    : 'bg-card border border-border text-foreground'
                }`}
              >
                {message?.flagged_offplatform && (
                  <div className="flex items-center gap-2 mb-2 text-xs">
                    <Icon name="AlertTriangle" className="w-3 h-3" />
                    <span>Flagged for review</span>
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap">{message?.message_text}</p>
                <p
                  className={`text-xs mt-1 ${
                    isSender ? 'text-white/70' : 'text-muted-foreground'
                  }`}
                >
                  {new Date(message?.created_at)?.toLocaleTimeString()}
                </p>
              </div>
            </div>
          );
        })
      )}
      <div ref={messagesEndRef} />
    </div>

    {/* Warning Banner */}
    {showWarning && (
      <div className="bg-warning/10 border-t border-warning p-3">
        <div className="flex items-start gap-2">
          <Icon name="AlertTriangle" className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground mb-1">
              Off-Platform Contact Detected
            </p>
            <p className="text-xs text-muted-foreground">
              Your message contains {detectedPatterns?.join(', ')}. Sharing contact information
              outside the platform violates our terms of service.
            </p>
          </div>
        </div>
      </div>
    )}

    {/* Input Area */}
    <div className="p-4 bg-card border-t border-border">
      <div className="flex gap-2">
        <textarea
          value={messageText}
          onChange={handleMessageChange}
          placeholder="Type your message..."
          className="flex-1 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground resize-none"
          maxLength={5000}
          rows="2"
          onKeyDown={(e) => {
            if (e?.key === 'Enter' && !e?.shiftKey) {
              e?.preventDefault();
              handleSendMessage();
            }
          }}
        />
        <Button
          onClick={handleSendMessage}
          disabled={!messageText?.trim()}
          iconName="Send"
          className="self-end"
        >
          Send
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  </div>
);

export default MessagingPanel;